import { DynamicTabs, PageWrapper, UserTopCover } from '@repo/ui';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

import { usePersonDetails } from './hooks';
import { PersonActivitiesTab } from './person-activities-tab';
import { PersonCompletedEvaluationsTab } from './person-completed-evaluations-tab';
import { PersonOverviewTab } from './person-overview-tab';
import { PersonRegisterTab } from './person-register-tab';
import { PersonValidatedActivitiesTab } from './person-validated-activities-tab';
import { getEnv } from 'shared/get-env';

export const PersonDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    person,
    loadingPerson,
    personProfiles,
    entitiesByPerson,
    requestGetPersonById,
    requestGetAllPersonProfile,
    requestGetAllEntitiesByPerson,
    requestGetAllCoursesByPerson,
    coursesByPerson,
  } = usePersonDetails();

  const uniqueRoleNames = [...new Set(personProfiles?.map((p) => p.name))];

  const hasStudentProfile = personProfiles?.some(
    ({ roleId }) => roleId === PROFILE_TYPE.STUDENT,
  );

  const canShowValidatedActivities = personProfiles?.some(
    ({ roleId }) =>
      roleId === PROFILE_TYPE.TEACHER ||
      roleId === PROFILE_TYPE.PRECEPTOR ||
      roleId === PROFILE_TYPE.ADMINISTRATIVE ||
      roleId === PROFILE_TYPE.COORDINATOR ||
      roleId === PROFILE_TYPE.ASSISTANT,
  );

  const canShowCompletedEvaluations = personProfiles?.some(
    ({ roleId }) =>
      roleId === PROFILE_TYPE.TEACHER ||
      roleId === PROFILE_TYPE.PRECEPTOR ||
      roleId === PROFILE_TYPE.COORDINATOR ||
      roleId === PROFILE_TYPE.ASSISTANT,
  );

  const getRoleIdToPersonOverview = () =>
    personProfiles?.find(({ roleId }) =>
      [
        PROFILE_TYPE.STUDENT,
        PROFILE_TYPE.TEACHER,
        PROFILE_TYPE.PRECEPTOR,
      ].includes(roleId),
    )?.roleId;

  useEffect(() => {
    requestGetPersonById({ id });
    requestGetAllPersonProfile({ id });
    requestGetAllEntitiesByPerson({ personHashId: id });
    requestGetAllCoursesByPerson({ personHashId: id });
  }, [id]);

  return (
    <PageWrapper title={`Perfil de ${person?.name}`}>
      <UserTopCover
        cover={getEnv('REACT_APP_ORGANIZATION_COVER_URL') || ''}
        avatar={person?.photoUrl}
        profiles={uniqueRoleNames}
        name={person?.name}
        iconLabels={[
          { icon: 'map-pin', label: person?.address?.city?.name || '-' },
        ]}
        expandableAvatar
        isLoading={loadingPerson}
        goBack={() => navigate(-1)}
      />

      <Row>
        <Col>
          <DynamicTabs
            tabs={[
              {
                id: '1',
                label: 'Visão Geral',
                tabContent: (
                  <PersonOverviewTab
                    profiles={personProfiles}
                    courses={coursesByPerson}
                    organizations={entitiesByPerson}
                    roleId={getRoleIdToPersonOverview()}
                    hasStudentProfile={hasStudentProfile}
                    hasTeacherOrPreceptorProfile={canShowValidatedActivities}
                  />
                ),
              },
              {
                id: '2',
                label: 'Cadastro',
                tabContent: (
                  <PersonRegisterTab
                    person={person}
                    isLoading={loadingPerson}
                    personProfiles={personProfiles}
                    requestGetPersonById={requestGetPersonById}
                    requestGetAllPersonProfile={requestGetAllPersonProfile}
                  />
                ),
              },
              ...(hasStudentProfile
                ? [
                    {
                      id: '3',
                      label: 'Atividades',
                      tabContent: (
                        <PersonActivitiesTab
                          courses={coursesByPerson}
                          profiles={personProfiles}
                          organizations={entitiesByPerson}
                        />
                      ),
                    },
                  ]
                : []),
              ...(canShowValidatedActivities
                ? [
                    {
                      id: '4',
                      label: 'Atividades Validadas',
                      tabContent: (
                        <PersonValidatedActivitiesTab
                          courses={coursesByPerson}
                          profiles={personProfiles}
                          organizations={entitiesByPerson}
                        />
                      ),
                    },
                  ]
                : []),
              ...(canShowCompletedEvaluations
                ? [
                    {
                      id: '5',
                      label: 'Avaliações Realizadas',
                      tabContent: (
                        <PersonCompletedEvaluationsTab
                          courses={coursesByPerson}
                          profiles={personProfiles}
                          organizations={entitiesByPerson}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            variant="pills"
            navClassName="animation-nav profile-nav gap-2 gap-lg-3 flex-grow-1"
            defaultActiveTabId="2"
            rerenderOnClickActiveTab={true}
          />
        </Col>
      </Row>
    </PageWrapper>
  );
};
